import type {
  AdminExerciseDraftExercise,
  AdminExerciseRequestCandidate,
  AdminExerciseRequestDetail,
  AdminExerciseRequestSample,
} from '@steado/admin-contract/exercise-requests';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Sparkles } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { SteadoLoader } from '@/components/shell/SteadoLoader';
import { BadgeCell, ChipsCell } from '@/components/table/cells';
import { FieldRow, FieldRows, Panel, TabSkeleton } from '@/components/panel/panels';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatRelative, humanise } from '@/lib/format';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { exercisesQueryOptions } from '@/screens/exercises/exercises-query';
import { TabError } from '@/screens/user-detail/TabError';

import {
  DRAFT_LABELS,
  DRAFT_TONES,
  RELATION_LABELS,
  RELATION_TONES,
  STATUS_LABELS,
  STATUS_TONES,
} from './exercise-request-labels';
import {
  draftExerciseRequest,
  exerciseRequestQueryOptions,
  fetchSamplePhoto,
  rejectExerciseRequest,
  resolveExerciseRequest,
} from './exercise-requests-query';

interface ExerciseRequestDrawerProps {
  readonly requestId: string;
  readonly onClose: () => void;
  /** Opens the create page prefilled from the draft. Navigating is the Route's job. */
  readonly onCreateFromDraft: (request: AdminExerciseRequestDetail) => void;
  /** Absent when the screen renders without a router, as a test does. */
  readonly onOpenExercise?: ((exerciseId: string) => void) | undefined;
}

const ADMIN_ONLY = 'Answering an exercise request requires the admin role.';

/** A catalog row an operator is about to call "this is it". */
interface PickedExercise {
  readonly id: string;
  readonly name: string;
}

/**
 * One exercise request, with everything needed to answer it: what the scans
 * wrote, the AI draft, and the catalog rows Steado may already have.
 *
 * "This is it" comes before "Create from draft" on purpose. A missing
 * exercise is often one the catalog has under another name, and resolving to
 * it writes the alias that stops the next scan treating it as a gap; a new
 * row next to an old one is a duplicate the planner will treat as different.
 */
export function ExerciseRequestDrawer({
  requestId,
  onClose,
  onCreateFromDraft,
  onOpenExercise,
}: ExerciseRequestDrawerProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(exerciseRequestQueryOptions(api, requestId));
  const reasonId = useId();

  const [picking, setPicking] = useState<PickedExercise | null>(null);
  const [isRejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const settled = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'exercise-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'exercise-request', requestId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
  };

  const draft = useMutation({
    mutationFn: () => draftExerciseRequest(api, requestId),
    onSuccess: settled,
    // Never retried automatically: each one is a model call.
    retry: false,
  });

  const resolve = useMutation({
    mutationFn: (exerciseId: string) =>
      resolveExerciseRequest(api, requestId, { exerciseId, created: false }),
    onSuccess: () => {
      setPicking(null);
      settled();
    },
    retry: false,
  });

  const reject = useMutation({
    mutationFn: () => rejectExerciseRequest(api, requestId, { reason: reason.trim() }),
    onSuccess: () => {
      setRejecting(false);
      setReason('');
      settled();
    },
    retry: false,
  });

  const request = query.data;
  const isOpen = request?.status === 'open';
  const answerable = isOpen && can.mutate;

  return (
    <>
      <Drawer
        title={request?.name ?? 'Exercise request'}
        {...(request === undefined
          ? {}
          : {
              subtitle: `${people(request.userCount)} · ${scans(request.scanCount)} · last seen ${formatRelative(request.lastSeenAt)}`,
            })}
        onClose={onClose}
        footer={
          request === undefined || !isOpen ? undefined : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  draft.mutate();
                }}
                disabled={!can.mutate || request.draftStatus === 'queued' || draft.isPending}
                title={
                  can.mutate ? 'Ask a strong model to draft the catalog row from these scans' : ADMIN_ONLY
                }
              >
                <Sparkles aria-hidden="true" className="size-3.5" />
                {request.draftStatus === 'queued' || draft.isPending
                  ? 'Drafting…'
                  : request.draftStatus === 'none'
                    ? 'Draft with AI'
                    : 'Draft again'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onCreateFromDraft(request);
                }}
                disabled={!can.mutate}
                title={
                  can.mutate
                    ? request.draft === null
                      ? 'Opens the create form with the name only; drafting first fills in the rest'
                      : undefined
                    : ADMIN_ONLY
                }
              >
                {request.draft === null ? 'Create exercise' : 'Create from draft'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRejecting(true);
                }}
                disabled={!can.mutate}
                title={can.mutate ? undefined : ADMIN_ONLY}
                className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              >
                Reject
              </Button>
            </>
          )
        }
      >
        {query.isPending ? <TabSkeleton rows={4} /> : null}
        {query.isError ? (
          <TabError message={query.error.message} onRetry={() => void query.refetch()} />
        ) : null}

        {request === undefined ? null : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <BadgeCell label={STATUS_LABELS[request.status]} tone={STATUS_TONES[request.status]} dot />
              <BadgeCell label={DRAFT_LABELS[request.draftStatus]} tone={DRAFT_TONES[request.draftStatus]} />
              {request.equipmentName === null ? null : (
                <span className="text-xs text-muted-foreground">on {request.equipmentName}</span>
              )}
            </div>

            {draft.isError ? (
              <Alert role="alert" tone="err">
                <AlertDescription>{draft.error.message}</AlertDescription>
              </Alert>
            ) : null}

            <Outcome request={request} onOpenExercise={onOpenExercise} />

            <DraftPanel request={request} />

            <Panel
              title="Steado may already have"
              description="Rows of the shared catalog this might be. Resolving to one maps every later scan of this name to it."
              bleed
            >
              <CandidateList
                candidates={request.candidates}
                canPick={answerable}
                onPick={(candidate) => {
                  setPicking({ id: candidate.id, name: candidate.name });
                }}
              />
              {answerable ? (
                <LibrarySearch
                  initialTerm={request.name}
                  onPick={(exercise) => {
                    setPicking(exercise);
                  }}
                />
              ) : null}
            </Panel>

            <Panel
              title={`What the scans wrote (${String(request.samples.length)})`}
              description="The newest scans that offered this exercise, and what the model told each user at the machine."
            >
              {request.samples.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No scans left behind it: they were deleted with their users.
                </p>
              ) : (
                <ol className="flex flex-col gap-4">
                  {request.samples.map((sample) => (
                    <SampleItem
                      key={sample.itemId}
                      requestId={request.id}
                      sample={sample}
                      canViewPhoto={can.mutate}
                    />
                  ))}
                </ol>
              )}
            </Panel>
          </div>
        )}
      </Drawer>

      {picking === null || request === undefined ? null : (
        <ConfirmDialog
          title="Resolve to this exercise?"
          body={
            <p>
              “{request.name}” will be closed as {picking.name}, and “{request.name}” becomes another name for
              it: later scans that write it get the catalog row instead of an unreviewed one.
            </p>
          }
          confirmLabel="This is it"
          onConfirm={() => {
            resolve.mutate(picking.id);
          }}
          onCancel={() => {
            setPicking(null);
            resolve.reset();
          }}
          isPending={resolve.isPending}
          error={resolve.isError ? resolve.error.message : undefined}
        />
      )}

      {isRejecting && request !== undefined ? (
        <ConfirmDialog
          title="Reject this request?"
          body={
            <div className="flex flex-col gap-3">
              <p>
                “{request.name}” gets no catalog row. Scans that write it keep counting here, and the reason
                is stored on the request for whoever meets the name next.
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor={reasonId} className="text-xs font-medium text-muted-foreground">
                  Reason
                </Label>
                <Input
                  id={reasonId}
                  value={reason}
                  maxLength={500}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                  placeholder="A brand name, not a movement"
                />
              </div>
            </div>
          }
          confirmLabel="Reject"
          destructive
          confirmDisabled={reason.trim() === ''}
          onConfirm={() => {
            reject.mutate();
          }}
          onCancel={() => {
            setRejecting(false);
            setReason('');
            reject.reset();
          }}
          isPending={reject.isPending}
          error={reject.isError ? reject.error.message : undefined}
        />
      ) : null}
    </>
  );
}

function Outcome({
  request,
  onOpenExercise,
}: {
  readonly request: AdminExerciseRequestDetail;
  readonly onOpenExercise?: ((exerciseId: string) => void) | undefined;
}) {
  if (request.status === 'open') return null;
  const resolved = request.resolvedExercise;
  return (
    <Panel
      title="Outcome"
      bleed
      action={
        resolved === null || onOpenExercise === undefined ? undefined : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenExercise(resolved.id);
            }}
          >
            Open exercise
          </Button>
        )
      }
    >
      <FieldRows>
        <FieldRow label="Status" value={STATUS_LABELS[request.status]} />
        {request.status === 'resolved' ? (
          <>
            <FieldRow
              label="Resolved to"
              value={resolved === null ? 'An exercise since deleted' : `${resolved.name} (${resolved.slug})`}
              {...(resolved === null ? { tone: 'muted' as const } : {})}
            />
            <FieldRow
              label="How"
              value={request.resolution === 'created' ? 'Created for this request' : 'Steado already had it'}
            />
          </>
        ) : (
          <FieldRow label="Reason" value={request.rejectionReason} />
        )}
      </FieldRows>
    </Panel>
  );
}

function DraftPanel({ request }: { readonly request: AdminExerciseRequestDetail }) {
  const { draft } = request;

  return (
    <Panel
      title="AI draft"
      description="Checked field by field against the create form; anything that failed was left out and is listed."
    >
      <div className="flex flex-col gap-3">
        {request.draftStatus === 'queued' ? (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <SteadoLoader />
            Writing the draft. It usually takes under a minute.
          </p>
        ) : null}
        {request.draftStatus === 'failed' ? (
          <Alert role="alert" tone="err">
            <AlertTitle>The draft failed</AlertTitle>
            <AlertDescription>
              {request.draftError ?? 'No reason was recorded.'} Draft again to retry.
            </AlertDescription>
          </Alert>
        ) : null}

        {draft === null ? (
          request.draftStatus === 'none' ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              No draft yet. Draft with AI asks a strong model to write the catalog row from these scans and
              the rows it might duplicate; you review it in the create form before anything is saved.
            </p>
          ) : null
        ) : (
          <>
            {draft.warnings.length === 0 ? null : (
              <Alert tone="warn" role="note">
                <AlertTitle>Check before creating</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4">
                    {draft.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            <DraftFields exercise={draft.exercise} />
            {draft.notes === null ? null : (
              <p className="text-[13px] leading-relaxed text-muted-foreground">Model’s note: {draft.notes}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {draft.model ?? 'Unknown model'} · {formatDateTime(draft.draftedAt)}
            </p>
          </>
        )}
      </div>
    </Panel>
  );
}

function DraftFields({ exercise }: { readonly exercise: AdminExerciseDraftExercise }) {
  const list = (values: readonly string[] | undefined) =>
    values === undefined ? null : <ChipsCell items={values.map(humanise)} max={6} />;
  const word = (value: string | null | undefined) =>
    value === undefined || value === null ? null : humanise(value);
  const yesNo = (value: boolean | null | undefined) =>
    value === undefined || value === null ? null : value ? 'Yes' : 'No';

  return (
    <div className="flex flex-col gap-3">
      <FieldRows>
        <FieldRow label="Name" value={exercise.name ?? null} />
        <FieldRow label="Category" value={word(exercise.category)} />
        <FieldRow label="Difficulty" value={word(exercise.difficulty)} />
        <FieldRow label="Primary muscles" value={list(exercise.primaryMuscles)} />
        <FieldRow label="Secondary muscles" value={list(exercise.secondaryMuscles)} />
        <FieldRow label="Equipment" value={list(exercise.equipment)} />
        <FieldRow label="Not for" value={list(exercise.contraindications)} />
        <FieldRow label="Weighted" value={yesNo(exercise.isWeighted)} />
        <FieldRow label="Movement pattern" value={word(exercise.movementPattern)} />
        <FieldRow label="Mechanic" value={word(exercise.mechanic)} />
      </FieldRows>
      {exercise.howToSteps === undefined || exercise.howToSteps.length === 0 ? null : (
        <ol className="list-decimal pl-5 text-sm leading-relaxed text-foreground">
          {exercise.howToSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function CandidateList({
  candidates,
  canPick,
  onPick,
}: {
  readonly candidates: readonly AdminExerciseRequestCandidate[];
  readonly canPick: boolean;
  readonly onPick: (candidate: AdminExerciseRequestCandidate) => void;
}) {
  if (candidates.length === 0) {
    return (
      <p className="border-t border-border px-[18px] py-3.5 text-sm text-muted-foreground">
        No catalog row matches the name, and no draft has judged any yet.
      </p>
    );
  }
  return (
    <ul>
      {candidates.map((candidate) => (
        <li
          key={candidate.id}
          className="flex items-start justify-between gap-3 border-t border-border px-[18px] py-3"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-foreground">{candidate.name}</span>
              <BadgeCell
                label={RELATION_LABELS[candidate.relation]}
                tone={RELATION_TONES[candidate.relation]}
              />
            </span>
            <span className="font-mono text-xs text-muted-foreground">{candidate.slug}</span>
            {candidate.note === null ? null : (
              <span className="text-[13px] leading-relaxed text-muted-foreground">{candidate.note}</span>
            )}
          </div>
          {canPick ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => {
                onPick(candidate);
              }}
            >
              This is it
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * The library's own list endpoint, so the picker needs no API of its own:
 * for when the row it means is not among the candidates.
 */
function LibrarySearch({
  initialTerm,
  onPick,
}: {
  readonly initialTerm: string;
  readonly onPick: (exercise: PickedExercise) => void;
}) {
  const { api } = useAuth();
  const inputId = useId();
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 260);
  const results = useQuery({
    ...exercisesQueryOptions(api, { search: debounced, activity: 'active' }),
    enabled: debounced.trim().length >= 2,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-border px-[18px] py-3.5">
      <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
        Not listed? Search the library
      </Label>
      <Input
        id={inputId}
        type="search"
        value={term}
        placeholder={initialTerm}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />
      {debounced.trim().length < 2 ? null : results.isPending ? (
        <p className="text-[13px] text-muted-foreground">Searching…</p>
      ) : (results.data?.items.length ?? 0) === 0 ? (
        <p className="text-[13px] text-muted-foreground">No active exercise matches that.</p>
      ) : (
        <ul className="max-h-[200px] overflow-y-auto rounded-md border border-border">
          {(results.data?.items ?? []).map((exercise) => (
            <li key={exercise.id}>
              <button
                type="button"
                onClick={() => {
                  onPick({ id: exercise.id, name: exercise.name });
                }}
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
              >
                <span className="min-w-0 flex-1 truncate">{exercise.name}</span>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{exercise.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SampleItem({
  requestId,
  sample,
  canViewPhoto,
}: {
  readonly requestId: string;
  readonly sample: AdminExerciseRequestSample;
  readonly canViewPhoto: boolean;
}) {
  return (
    <li className="flex flex-col gap-2 border-b border-border pb-4 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] text-muted-foreground">
          {sample.equipmentName ?? 'Unnamed equipment'} · {formatDateTime(sample.capturedAt)}
        </span>
        <ChipsCell items={sample.primaryMuscles.map(humanise)} max={4} />
      </div>
      {sample.howToSteps.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">No steps were written.</p>
      ) : (
        <ol className="list-decimal pl-5 text-sm leading-relaxed text-foreground">
          {sample.howToSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
      {sample.hasPhoto ? (
        canViewPhoto ? (
          <SamplePhoto requestId={requestId} scanId={sample.scanId} />
        ) : (
          <p className="text-xs text-muted-foreground">Viewing the photo requires the admin role.</p>
        )
      ) : (
        <p className="text-xs text-muted-foreground">The photo has been deleted.</p>
      )}
    </li>
  );
}

/**
 * The photo, on an explicit click only. It can show other people at the gym,
 * so it is never prefetched, every view is recorded on the server with whose
 * scan it was, and the bytes live in an object URL that is released when the
 * photo is hidden or the drawer closes.
 */
function SamplePhoto({ requestId, scanId }: { readonly requestId: string; readonly scanId: string }) {
  const { api } = useAuth();
  const [url, setUrl] = useState<string | null>(null);

  const load = useMutation({
    mutationFn: () => fetchSamplePhoto(api, requestId, scanId),
    onSuccess: (blob) => {
      setUrl(URL.createObjectURL(blob));
    },
    retry: false,
  });

  useEffect(
    () => () => {
      if (url !== null) URL.revokeObjectURL(url);
    },
    [url],
  );

  if (url !== null) {
    return (
      <figure className="flex flex-col gap-2">
        <img
          src={url}
          alt="The scanned equipment"
          className="w-full max-w-[420px] rounded-xl border border-border"
        />
        <figcaption>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => {
              setUrl(null);
            }}
          >
            Hide photo
          </Button>
        </figcaption>
      </figure>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={() => {
          load.mutate();
        }}
        disabled={load.isPending}
      >
        <ImageIcon aria-hidden="true" className="size-3.5" />
        {load.isPending ? 'Loading…' : 'Show photo'}
      </Button>
      <span className={cn('text-xs', load.isError ? 'text-err-fg' : 'text-muted-foreground')}>
        {load.isError ? load.error.message : 'Viewing is recorded, with whose scan it was.'}
      </span>
    </div>
  );
}

function people(count: number): string {
  return `${String(count)} ${count === 1 ? 'person' : 'people'}`;
}

function scans(count: number): string {
  return `${String(count)} scan${count === 1 ? '' : 's'}`;
}
